import React, { useState, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import {
    Card,
    CardContent,
    Typography,
    Grid,
    Box,
} from '@mui/material';
import { CircularLoading } from '../Common';
import { DownArrow, UpArrow } from "../../Icons/Icons";
import { trakrefBlue, Grey2, Grey1 } from '../../color_utils';
import { trakrefFontFamily, trakrefFontFamilyBold } from '../../utils';
import { Link } from 'react-router-dom';
import "./DetailPanel.css";

export const DetailPanelData = ({
    title = '',
    data = '',
    link = false,
    linkId = 0,
    image = null,
    isClickable = false,
    isLastRow = false,
    onClick,
    createAlertButton = null,
    render = null,
}) => {
    const [hasMultiple, setHasMultiple] = useState(false);

    useEffect(() => {
        if (Array.isArray(data)) {
            setHasMultiple(true)
        }
    }, [data]);

    return (
        <div
            tabindex="0"
            className={`detail-item ${(link || isClickable) ? 'clickable underline_hover' : ''}`}
            onClick={(onClick != null && isClickable) ? () => onClick() : null}
            style={{ marginBottom: isLastRow ? "0px" : undefined }}
        >
            <p className="heading">
                {title ? title.toUpperCase() : null}
            </p>

            {render && render()}

            {hasMultiple == false && link == false ? (
                <div className="detailPanelData">
                    <p className="subheading">
                        {(data === null || data === "") && !render
                            ? "-"
                            : data
                        }
                    </p>
                    {
                        createAlertButton != null ? createAlertButton : null
                    }
                    {
                        image != null && (
                            <img src={image} />
                        )
                    }
                </div>
            ) : link != false ? (
                <Link className="detail-link"
                    title={data}
                    to={link + linkId}
                >
                    {data}
                </Link>
            ) : (
                data.map((obj) => (
                    <div>
                        <p className={`subheading`}>
                            {obj}
                        </p>
                        {
                            image != null && (
                                <img src={image} />
                            )
                        }
                    </div>
                ))
            )}
        </div>
    );
};

export const DetailPanelGroup = ({
    children,
    title
}) => {
    return (
        <>
            <Box sx={{
                display: { xs: 'block', lg: 'none' },
                width: "calc(100% - 6%)",
                backgroundColor: "#e0e0e0",
                height: "1px",
                margin: { xs: "16px 0px 24px 0px", md: "0px 0px 24px 0px" },
            }} />
            <div style={{
                display: "flex",
                width: "100%",
                flexDirection: "column",
                justifyContent: "center",
                gap: "10px"
            }}>
                <Box sx={{
                    color: Grey1,
                    fontFamily: { xs: trakrefFontFamilyBold, lg: trakrefFontFamily },
                    fontSize: "12px",
                    fontStyle: "normal",
                    fontWeight: 600,
                    lineHeight: "normal",
                    letterSpacing: "0.7px",
                    textTransform: "capitalize"
                }}>
                    {title}
                </Box>
                <div style={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    placeContent: "center flex-start",
                    alignItems: "flex-start",
                    alignContent: "flex-start"
                }}>
                    {children}
                </div>
            </div>
        </>
    )
}

export const DetailPanel = ({
    increasedHeight = false,
    disableDefaultHeight = false,
    autoHeight = false,
    children,
    expandable = false,
    title,
    setHeaderCollapsed = () => { },
    pwaActionItemsCount = 0,
}) => {
    const [collapsed, setCollapsed] = useState(true);

    const toggleExpansion = () => {
        setCollapsed(!collapsed);
        setHeaderCollapsed(!collapsed);
    }

    // autoHeight grows with the content instead of clipping it at a fixed height
    const increasedHeightClass =
        autoHeight ? 'expandedDP'
            : disableDefaultHeight ?
            increasedHeight ? 'altDP2' : 'altDP'
            : increasedHeight ? 'normalDP2' : 'normalDP';

    return (
        <div className='detailPanelBackground'>
            {
                title && (
                    <div className='title-container'>
                        <span className='title'>{title}</span>
                    </div>
                )
            }
            <div
                className={`${collapsed ? increasedHeightClass : 'expandedDP'} detailPanelContainer flex f-row`}
            >
                <div className="divider div1"></div>
                <div className="divider div2"></div>

                {children}

            </div>

            {expandable === true && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        lineHeight: 0,
                        height: {
                            xs: 'auto',
                            md: collapsed ? (pwaActionItemsCount === 2 ? '140px' : pwaActionItemsCount === 1 ? '85px' : 'auto') : 'auto',
                            lg: 'auto'
                        }
                    }}
                >
                    {collapsed ? (
                        <DownArrow
                            className="clickable"
                            color="lightgray"
                            size={30}
                            onClick={toggleExpansion}
                        />
                    ) : (
                        <UpArrow
                            className="clickable"
                            color="lightgray"
                            size={30}
                            onClick={toggleExpansion}
                        />
                    )}
                </Box>
            )}

        </div>
    );
};

export const DetailPanelButton = ({ text, onClick, isVisible = true }) => {
    return (
        <span
            style={{
                color: "#00859B",
                fontFamily: trakrefFontFamily,
                fontSize: "12px",
                fontStyle: "normal",
                fontWeight: "600",
                lineHeight: "normal",
                letterSpacing: "0.7px",
                textDecorationLine: "underline",
                textTransform: "capitalize",
                cursor: "pointer",
                ...(!isVisible && { visibility: 'hidden'})
            }}
            onClick={onClick}>
            {text}
        </span >
    );
}

export const DetailPanelMUI = ({
    children,
    raised,
}) => (
    <Box sx={{ minWidth: 275 }}>
        <Card variant="outlined" raised={raised}>
            <CardContent sx={{ padding: { xs: '0px !important', lg: '0px' } }}>
                <Grid container spacing={3}
                    sx={{
                        marginLeft: { xs: '0px !important', lg: '0px' },
                        marginTop: { xs: '0px !important', lg: '0px' },
                        marginBottom: { xs: '20px !important', lg: '0px' },
                    }}>
                    {children}
                </Grid>
            </CardContent>
        </Card>
    </Box>
);

export const DetailPanelMUIData = ({
    title = '',
    value = '',
    onClick = null,
    loading = false,
}) => (
    <Grid
        item
        sx={{
            paddingLeft: { xs: '20px !important', md: '4%' },
            paddingTop: { xs: '15px !important', md: '0px' },
            maxWidth: { xs: '45% !important', md: '0%' },
            ...(!isMobile && { paddingLeft: '4%' })
        }}
        xs={6}
        md={4}
        lg={4}
    >
        <Typography
            variant="overline"
            
            style={{
                color: trakrefBlue,
                fontSize: '12px',
                fontFamily: trakrefFontFamily,
                fontWeight: 'bolder',
                textTransform: 'uppercase',
            }}
        >
            {title}
        </Typography>
        <Typography
            variant="h4"
            style={{
                color: Grey2,
                fontSize: '12px',
                fontFamily: trakrefFontFamily
            }}
            sx={{
                '&.MuiTypography-root:hover': {
                    textDecoration: !(onClick && value) ? 'none' : 'underline',
                    cursor: !(onClick && value) ? '' : 'pointer',
                }
            }}
            onClick={onClick}
        >
            {loading
                ? <CircularLoading size={20} marginTop={0} marginLeft={21} />
                : value
                    ? value
                    : "-"
            }
        </Typography>
    </Grid>
);
